import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { LoadedSchema } from '@cli-types/core-engine/schema-reader.js'
import { clearTombstone } from '@core-engine/snapshots/store/clear-tombstone.js'
import { hasTombstone } from '@core-engine/snapshots/store/has-tombstone.js'
import { applyGeneratedFiles } from '@next-adapter/snapshots/apply/apply-generated-files.js'
import { maybeBuildRenamePlan } from './maybe-build-rename-plan.js'
import { printSchemaDiff } from './print-schema-diff.js'
import { runPipeline } from './run-pipeline.js'
import { validateIntegrationRequirements } from './validate-integration-requirements.js'

export async function applySchemaGeneration(
  loaded: LoadedSchema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: { force: boolean; acceptGenerated?: boolean; interactive: boolean }
) {
  const scope = loaded.schema.name
  const tombstoneCleared = hasTombstone(cwd, scope)
  if (tombstoneCleared) {
    clearTombstone(cwd, scope)
  }

  validateIntegrationRequirements(loaded, config)
  printSchemaDiff(scope, loaded, cwd)

  const pipelineResult = runPipeline(loaded, cwd, config, options)
  if (!pipelineResult.success) {
    throw new Error(pipelineResult.errors.join('; '))
  }

  const renamePlan = await maybeBuildRenamePlan(loaded, cwd, config, pipelineResult.files, options)

  const summary = await applyGeneratedFiles({
    cwd,
    config,
    scope,
    schemaJson: loaded.schema,
    generatedFiles: pipelineResult.files,
    force: options.force,
    acceptGenerated: options.acceptGenerated,
    interactive: options.interactive,
    renamePlan
  })

  if (tombstoneCleared) {
    summary.tombstoneCleared = scope
  }

  return {
    fileCount: pipelineResult.files.length,
    summary
  }
}
